import type { GetHistoryEntry } from '@repo/schema/history'
import { formatSnakeCase } from '@repo/sdk/helpers'

import type { HistoryEntity } from './entity.ts'

const fieldActionLabels: Record<string, string> = {
  delivery_date: 'Delivery date changed',
  status: 'Status changed',
}

export const getFieldTitle = (entry: GetHistoryEntry, entity: HistoryEntity) => {
  const actionLabel = entry.field
    ? (entity.fieldLabels?.[entry.field] ?? fieldActionLabels[entry.field])
    : undefined
  if (actionLabel) return actionLabel

  const label = entry.field_label || entry.field
  return label ? `${label} changed` : 'Field changed'
}

const eventLabels: Partial<Record<GetHistoryEntry['event'], string>> = {
  assignee_added: 'Assignee added',
  assignee_removed: 'Assignee removed',
  commission_assignee_added: 'Commission assignee added',
  commission_assignee_removed: 'Commission assignee removed',
  company_merged: 'Company merged',
  document_deleted: 'Document deleted',
  document_uploaded: 'Document uploaded',
  status_changed: 'Status changed',
}

const entityEvents = new Set(['created', 'deleted', 'duplicated', 'updated'])
const childEventVerbs = new Map([
  ['created', 'Added'],
  ['deleted', 'Removed'],
])

// Created and deleted events also record an entity's children, such as a load's flags or a company's contacts.
export const getEventLabel = (
  { event, subject }: Pick<GetHistoryEntry, 'event' | 'subject'>,
  entity: HistoryEntity,
) => {
  const childVerb = subject && subject.type !== entity.name ? childEventVerbs.get(event) : undefined
  if (subject && childVerb) {
    const noun = entity.subjectLabels?.[subject.type]
    return noun ? `${noun} ${childVerb.toLowerCase()}` : childVerb
  }
  if (entityEvents.has(event)) return `${entity.name} ${event}`
  return (
    entity.eventLabels?.[event] ??
    eventLabels[event] ??
    formatSnakeCase(event, { capitalize: 'first', space: 'space' })
  )
}
